import PropTypes from "prop-types";
import React from "react";

/**
 * Reusable Button Component
 *
 * Supports three variants:
 * - primary (solid accent color)
 * - secondary (neutral gray background)
 * - tertiary (white background, bordered)
 *
 * Can render optional icons (left/right) and custom background or text colors.
 *
 * @component
 * @example
 * <Button variant="primary" onClick={handleSave} iconLeft={<Save size={16} />}>
 *   Save Changes
 * </Button>
 *
 * @param {Object} props                        - Component props
 * @param {('button'|'submit'|'reset')} [props.type='button'] - Button type
 * @param {('primary'|'secondary'|'tertiary')} [props.variant='primary'] - Visual style
 * @param {React.ReactNode} [props.children] - Button label/content
 * @param {Function} [props.onClick]         - Click event handler
 * @param {boolean} [props.disabled=false] - Disable state
 * @param {React.ReactNode} [props.iconLeft] - Icon to render on the left side
 * @param {React.ReactNode} [props.iconRight] - Icon to render on the right side
 * @param {string} [props.bgColor] - Override background color
 * @param {string} [props.textColor] - Override text color
 * @param {string} [props.className] - Override the class (perhaps this makes some of the above styling props redudant)
 * @returns {JSX.Element} A styled, accessible button component
 */
const Button = ({
  type = "button",
  variant = "primary",
  children,
  onClick,
  disabled = false,
  iconLeft,
  iconRight,
  bgColor,
  textColor,
  className
}) => {
  const baseStyles =
    "inline-flex items-center justify-center gap-2 px-5 py-2.5 font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1";

  const variants = {
    primary:
      "bg-indigo-600 hover:bg-indigo-700 text-white focus:ring-indigo-500",
    secondary:
      "bg-gray-100 hover:bg-gray-200 text-gray-700 focus:ring-gray-400",
    tertiary:
      "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 focus:ring-gray-300",
  };

  const classes = [
    baseStyles,
    variants[variant],
    bgColor ? bgColor : "",
    textColor ? textColor : "",
    disabled ? "opacity-60 cursor-not-allowed" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={className || classes}
    >
      {iconLeft && <span className="flex items-center">{iconLeft}</span>}
      <span>{children}</span>
      {iconRight && <span className="flex items-center">{iconRight}</span>}
    </button>
  );
};

Button.propTypes = {
  type: PropTypes.oneOf(["button", "submit", "reset"]),
  variant: PropTypes.oneOf(["primary", "secondary", "tertiary"]),
  children: PropTypes.node.isRequired,
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
  iconLeft: PropTypes.node,
  iconRight: PropTypes.node,
  bgColor: PropTypes.string,
  textColor: PropTypes.string,
};

export default Button;
